import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar, Users, Clock, DollarSign, Plus, Eye, CheckCircle, XCircle, AlertTriangle, Paperclip, Edit, Tag, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useRole } from "@/hooks/useRole";
import { toast } from "sonner";
import { FileUploadZone } from "@/components/proposals/FileUploadZone";

interface CourseProposal {
  id: string;
  title: string;
  description: string;
  status: string;
  duration_hours: number;
  max_participants: number;
  is_online: boolean;
  location?: string;
  budget_estimate?: number;
  created_at: string;
  proposed_by: string;
  proposer_name?: string;
  reviewer_id?: string;
  review_notes?: string;
  subject_area?: string;
  proficiency_level?: string;
  teaching_standards?: string[];
  target_groups?: string[];
  target_job_types?: string[];
  target_departments?: string[];
  target_grade_levels?: string[];
  course_tags?: string[];
}

interface CourseTag {
  id: string;
  name: string;
  description?: string;
  color: string;
  is_featured: boolean;
}

export default function CourseProposals() {
  const { user } = useAuth();
  const { isAdmin, isInstructor } = useRole();
  const [proposals, setProposals] = useState<CourseProposal[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedProposal, setSelectedProposal] = useState<CourseProposal | null>(null);
  const [activeTab, setActiveTab] = useState('my-proposals');
  const [isEditing, setIsEditing] = useState(false);
  const [editingProposal, setEditingProposal] = useState<CourseProposal | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    duration_hours: '',
    max_participants: '',
    is_online: false,
    location: '',
    justification: '',
    learning_objectives: '',
    target_audience: '',
    materials_needed: '',
    budget_estimate: '',
    subject_area: '',
    proficiency_level: '',
    proposer_name: '',
    teaching_standards: [] as string[],
    target_groups: [] as string[],
    target_job_types: [] as string[],
    target_departments: [] as string[],
    target_grade_levels: [] as string[],
    course_tags: [] as string[]
  });

  const [attachments, setAttachments] = useState<any[]>([]);
  const [showNewSubjectDialog, setShowNewSubjectDialog] = useState(false);
  const [newSubjectName, setNewSubjectName] = useState('');
  const [addingSubject, setAddingSubject] = useState(false);

  // Reference data
  const [subjects, setSubjects] = useState<any[]>([]);
  const [teachingStandards, setTeachingStandards] = useState<any[]>([]);
  const [userGroups, setUserGroups] = useState<any[]>([]);
  const [userState, setUserState] = useState<string>('');
  const [availableTags, setAvailableTags] = useState<CourseTag[]>([]);

  useEffect(() => {
    fetchProposals();
    fetchReferenceData();
    
    // Subscribe to real-time updates for proposals
    const channel = supabase
      .channel('course-proposals-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'course_proposals'
        },
        (payload) => {
          console.log('Real-time proposal update:', payload);
          // Refresh proposals when any proposal changes
          fetchProposals();
        }
      )
      .subscribe();
    
    // Auto-populate user name when component mounts
    if (user) {
      const fetchUserName = async () => {
        try {
          const { data: profile } = await supabase
            .from('profiles')
            .select('first_name, last_name')
            .eq('id', user.id)
            .single();
          
          if (profile) {
            const fullName = `${profile.first_name || ''} ${profile.last_name || ''}`.trim();
            setFormData(prev => ({
              ...prev,
              proposer_name: fullName || user.email || ''
            }));
          }
        } catch (error) {
          console.error('Error fetching user profile:', error);
          // Fallback to email if profile fetch fails
          setFormData(prev => ({
            ...prev,
            proposer_name: user.email || ''
          }));
        }
      };
      
      fetchUserName();
    }

    // Cleanup subscription on unmount
    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  const fetchReferenceData = async () => {
    try {
      // Get user's state from profile
      const { data: profile } = await supabase
        .from('profiles')
        .select('state')
        .eq('id', user?.id)
        .single();

      const userState = profile?.state;
      setUserState(userState);

      // Fetch subjects
      const { data: subjectsData } = await supabase
        .from('subjects')
        .select('*')
        .order('name');
      
      // Fetch teaching standards - filter by user's state + national standards
      let standardsQuery = supabase
        .from('teaching_standards')
        .select('*');
      
      if (userState) {
        // Get both national standards and user's state standards
        standardsQuery = standardsQuery.or(`state.eq.National,state.eq.${userState}`);
      } else {
        // If no state, show only national standards
        standardsQuery = standardsQuery.eq('state', 'National');
      }
      
      const { data: standardsData } = await standardsQuery.order('state, category, title');
      
      // Fetch user groups
      const { data: groupsData } = await supabase
        .from('user_groups')
        .select('*')
        .order('name');

      // Fetch course tags (only for admins/instructors)
      let tagsData = [];
      if (isAdmin || isInstructor) {
        const { data: tags } = await supabase
          .from('course_tags')
          .select('*')
          .order('name');
        tagsData = tags || [];
      }

      setSubjects(subjectsData || []);
      setTeachingStandards(standardsData || []);
      setUserGroups(groupsData || []);
      setAvailableTags(tagsData);
    } catch (error) {
      console.error('Error fetching reference data:', error);
    }
  };

  const fetchProposals = async () => {
    try {
      console.log('Fetching course proposals with review notes...');
      const { data, error } = await supabase
        .from('course_proposals')
        .select('*') // Get all fields including review_notes
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      console.log('Raw proposals data:', data);
      setProposals(data as CourseProposal[]);
    } catch (error) {
      console.error('Error fetching proposals:', error);
      toast.error('Failed to load course proposals');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      console.log(isEditing ? 'Updating course proposal...' : 'Submitting course proposal...');
      
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('district_id, first_name, last_name')
        .eq('id', user?.id)
        .single();

      if (profileError) {
        console.error('Error fetching profile:', profileError);
        throw new Error('Unable to fetch user profile');
      }

      console.log('User profile:', profile);

      // Prepare the proposal data
      const proposalData = {
        title: formData.title,
        description: formData.description,
        proposed_by: user?.id,
        district_id: profile?.district_id || null, // Allow null if user has no district
        duration_hours: parseFloat(formData.duration_hours),
        max_participants: parseInt(formData.max_participants),
        is_online: formData.is_online,
        location: formData.location || null,
        justification: formData.justification,
        learning_objectives: formData.learning_objectives.split('\n').filter(obj => obj.trim()),
        target_audience: formData.target_audience.split(',').map(aud => aud.trim()),
        materials_needed: formData.materials_needed,
        budget_estimate: formData.budget_estimate ? parseFloat(formData.budget_estimate) : null,
        subject_area: formData.subject_area || null,
        proficiency_level: formData.proficiency_level || null,
        teaching_standards: formData.teaching_standards,
        target_groups: formData.target_groups,
        target_job_types: formData.target_job_types,
        target_departments: formData.target_departments,
        target_grade_levels: formData.target_grade_levels,
        course_tags: formData.course_tags,
        attachments: attachments,
        // Store proposer name in proposal for reference
        proposer_name: formData.proposer_name
      };

      console.log('Proposal data:', proposalData);

      if (isEditing && editingProposal) {
        // Update existing proposal
        const updateData = {
          ...proposalData,
          // Reset status to pending for re-review if it was approved/rejected
          status: editingProposal.status === 'approved' || editingProposal.status === 'rejected' ? 'pending' : editingProposal.status,
          review_notes: editingProposal.status === 'approved' || editingProposal.status === 'rejected' ? 'Proposal updated and re-submitted for review' : editingProposal.review_notes
        };

        const { error } = await supabase
          .from('course_proposals')
          .update(updateData)
          .eq('id', editingProposal.id);

        if (error) {
          console.error('Database error:', error);
          throw error;
        }

        toast.success('Course proposal updated successfully!');
      } else {
        // Insert new proposal
        const { error } = await supabase
          .from('course_proposals')
          .insert(proposalData);

        if (error) {
          console.error('Database error:', error);
          throw error;
        }

        toast.success('Course proposal submitted successfully!');
      }

      setIsDialogOpen(false);
      setIsEditing(false);
      setEditingProposal(null);
      fetchProposals();
      
      // Reset form
      setFormData({
        title: '',
        description: '',
        duration_hours: '',
        max_participants: '',
        is_online: false,
        location: '',
        justification: '',
        learning_objectives: '',
        target_audience: '',
        materials_needed: '',
        budget_estimate: '',
        subject_area: '',
        proficiency_level: '',
        proposer_name: formData.proposer_name, // Keep the name for next submission
        teaching_standards: [],
        target_groups: [],
        target_job_types: [],
        target_departments: [],
        target_grade_levels: [],
        course_tags: []
      });
      setAttachments([]);
    } catch (error: any) {
      console.error('Error submitting proposal:', error);
      toast.error(`Failed to ${isEditing ? 'update' : 'submit'} course proposal: ${error.message || 'Unknown error'}`);
    }
  };

  const handleEditProposal = (proposal: CourseProposal) => {
    setEditingProposal(proposal);
    setIsEditing(true);
    
    // Pre-populate form with existing data
    setFormData({
      title: proposal.title,
      description: proposal.description || '',
      duration_hours: proposal.duration_hours?.toString() || '',
      max_participants: proposal.max_participants?.toString() || '',
      is_online: proposal.is_online || false,
      location: proposal.location || '',
      justification: (proposal as any).justification || '',
      learning_objectives: Array.isArray((proposal as any).learning_objectives) 
        ? (proposal as any).learning_objectives.join('\n') 
        : (proposal as any).learning_objectives || '',
      target_audience: Array.isArray((proposal as any).target_audience) 
        ? (proposal as any).target_audience.join(', ') 
        : (proposal as any).target_audience || '',
      materials_needed: (proposal as any).materials_needed || '',
      budget_estimate: proposal.budget_estimate?.toString() || '',
      subject_area: proposal.subject_area || '',
      proficiency_level: proposal.proficiency_level || '',
      proposer_name: proposal.proposer_name || '',
      teaching_standards: proposal.teaching_standards || [],
        target_groups: (proposal as any).target_groups || [],
        target_job_types: (proposal as any).target_job_types || [],
        target_departments: (proposal as any).target_departments || [],
        target_grade_levels: (proposal as any).target_grade_levels || [],
        course_tags: proposal.course_tags || []
    });
    
    // Set attachments if they exist
    setAttachments((proposal as any).attachments || []);
    setIsDialogOpen(true);
  };

  const handleAddNewSubject = async () => {
    if (!newSubjectName.trim()) {
      toast.error('Please enter a subject name');
      return;
    }

    setAddingSubject(true);
    try {
      const { data, error } = await supabase
        .from('subjects')
        .insert({
          name: newSubjectName.trim(),
          description: `Subject area: ${newSubjectName.trim()}`
        })
        .select()
        .single();

      if (error) throw error;

      // Add the new subject to the subjects list
      setSubjects(prev => [...prev, data]);
      
      // Select the new subject
      setFormData(prev => ({ ...prev, subject_area: data.name }));
      
      // Close dialog and reset
      setShowNewSubjectDialog(false);
      setNewSubjectName('');
      
      toast.success('Subject area created successfully!');
    } catch (error: any) {
      console.error('Error adding subject:', error);
      toast.error(`Failed to add subject: ${error.message || 'Unknown error'}`);
    } finally {
      setAddingSubject(false);
    }
  };

  const getStatusBadge = (status: string, reviewNotes?: string) => {
    console.log('Status Badge Debug:', { status, reviewNotes, hasNotes: !!reviewNotes });
    
    const variants = {
      pending: { variant: 'secondary' as const, icon: Clock, color: 'text-yellow-600' },
      under_review: { variant: 'secondary' as const, icon: Eye, color: 'text-blue-600' },
      approved: { variant: 'default' as const, icon: CheckCircle, color: 'text-green-600' },
      rejected: { variant: 'destructive' as const, icon: XCircle, color: 'text-red-600' },
      needs_revision: { variant: 'secondary' as const, icon: AlertTriangle, color: 'text-orange-600' }
    };

    const config = variants[status as keyof typeof variants];
    const Icon = config.icon;
    
    const badge = (
      <Badge variant={config.variant} className="gap-1">
        <Icon className={`h-3 w-3 ${config.color}`} />
        {status.replace('_', ' ').toUpperCase()}
      </Badge>
    );

    // Show hover card for any status with review notes (including pending with feedback)
    if (reviewNotes && reviewNotes.trim()) {
      console.log('Showing hover card for:', status);
      return (
        <HoverCard>
          <HoverCardTrigger asChild>
            <div className="cursor-help inline-block">
              {badge}
            </div>
          </HoverCardTrigger>
          <HoverCardContent className="w-80" side="left">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${
                  status === 'approved' ? 'bg-green-500' :
                  status === 'rejected' ? 'bg-red-500' : 'bg-orange-500'
                }`} />
                <h4 className="text-sm font-semibold">
                  {status === 'pending' ? 'Previous Feedback' : 'Reviewer Feedback'}
                </h4>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {reviewNotes}
              </p>
            </div>
          </HoverCardContent>
        </HoverCard>
      );
    }

    console.log('No hover card - no review notes for:', status);
    return badge;
  };

  const myProposals = proposals.filter(p => p.proposed_by === user?.id);
  const allProposals = proposals;

  if (loading) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center">Loading course proposals...</div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Course Proposals</h1>
          <p className="text-muted-foreground">Submit and manage professional development course proposals</p>
        </div>
        
        <Dialog open={isDialogOpen} onOpenChange={(open) => {
          setIsDialogOpen(open);
          if (!open) {
            setIsEditing(false);
            setEditingProposal(null);
            // Reset form when closing
            setFormData({
              title: '',
              description: '',
              duration_hours: '',
              max_participants: '',
              is_online: false,
              location: '',
              justification: '',
              learning_objectives: '',
              target_audience: '',
              materials_needed: '',
              budget_estimate: '',
              subject_area: '',
              proficiency_level: '',
              proposer_name: formData.proposer_name,
              teaching_standards: [],
              target_groups: [],
              target_job_types: [],
              target_departments: [],
              target_grade_levels: [],
              course_tags: []
             });
             setAttachments([]);
           }
         }}>
          <DialogTrigger asChild>
            <Button className="gap-2">
              <Plus className="h-4 w-4" />
              New Proposal
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{isEditing ? 'Edit Course Proposal' : 'Submit Course Proposal'}</DialogTitle>
              <DialogDescription>
                {isEditing ? 'Update your course proposal details' : 'Propose a new professional development course for your district'}
              </DialogDescription>
            </DialogHeader>
            
             <form onSubmit={handleSubmit} className="space-y-4">
               <div className="grid grid-cols-2 gap-4">
                 <div className="col-span-2">
                   <Label htmlFor="proposer_name">Proposer Name *</Label>
                   <Input
                     id="proposer_name"
                     value={formData.proposer_name}
                     onChange={(e) => setFormData({...formData, proposer_name: e.target.value})}
                     required
                     placeholder="Your full name"
                   />
                 </div>
                 
                 <div className="col-span-2">
                   <Label htmlFor="title">Course Title *</Label>
                   <Input
                     id="title"
                     value={formData.title}
                     onChange={(e) => setFormData({...formData, title: e.target.value})}
                     required
                   />
                 </div>
                
                <div className="col-span-2">
                  <Label htmlFor="description">Course Description *</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    rows={3}
                    required
                  />
                </div>
                
                <div>
                  <Label htmlFor="duration_hours">Duration (Hours) *</Label>
                  <Input
                    id="duration_hours"
                    type="number"
                    step="0.5"
                    value={formData.duration_hours}
                    onChange={(e) => setFormData({...formData, duration_hours: e.target.value})}
                    required
                  />
                </div>
                
                <div>
                  <Label htmlFor="max_participants">Max Participants</Label>
                  <Input
                    id="max_participants"
                    type="number"
                    value={formData.max_participants}
                    onChange={(e) => setFormData({...formData, max_participants: e.target.value})}
                  />
                </div>
                
                <div className="col-span-2">
                  <Label htmlFor="is_online">Delivery Format</Label>
                  <Select value={formData.is_online ? 'online' : 'in-person'} onValueChange={(value) => setFormData({...formData, is_online: value === 'online'})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="in-person">In-Person</SelectItem>
                      <SelectItem value="online">Online</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {!formData.is_online && (
                  <div className="col-span-2">
                    <Label htmlFor="location">Location</Label>
                    <Input
                      id="location"
                      value={formData.location}
                      onChange={(e) => setFormData({...formData, location: e.target.value})}
                      placeholder="Enter venue or address"
                    />
                  </div>
                )}
                
                 {/* Enhanced Fields */}
                 <div>
                   <Label htmlFor="subject_area">Subject Area</Label>
                   <div className="flex gap-2">
                     <Select value={formData.subject_area} onValueChange={(value) => setFormData({...formData, subject_area: value})}>
                       <SelectTrigger className="flex-1">
                         <SelectValue placeholder="Select subject area" />
                       </SelectTrigger>
                       <SelectContent>
                         {subjects.map((subject) => (
                           <SelectItem key={subject.id} value={subject.name}>
                             {subject.name}
                           </SelectItem>
                         ))}
                       </SelectContent>
                     </Select>
                     <Button
                       type="button"
                       variant="outline"
                       size="sm"
                       onClick={() => setShowNewSubjectDialog(true)}
                       className="whitespace-nowrap"
                     >
                       <Plus className="h-4 w-4 mr-1" />
                       Add New
                     </Button>
                   </div>
                 </div>
                
                <div>
                  <Label htmlFor="proficiency_level">Program Proficiency Level</Label>
                  <Select value={formData.proficiency_level} onValueChange={(value) => setFormData({...formData, proficiency_level: value})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select proficiency level" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="beginner">Beginner</SelectItem>
                      <SelectItem value="intermediate">Intermediate</SelectItem>
                      <SelectItem value="advanced">Advanced</SelectItem>
                      <SelectItem value="expert">Expert</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="col-span-2">
                  <div className="flex items-center justify-between mb-2">
                    <Label>Teaching Standards</Label>
                    {userState && (
                      <div className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded">
                        Showing {userState} + National Standards
                      </div>
                    )}
                    {!userState && (
                      <div className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded">
                        Showing National Standards Only
                      </div>
                    )}
                  </div>
                   <div className="border rounded p-3 bg-muted/30">
                    <div className="max-h-40 overflow-y-auto space-y-3">
                      {/* Group standards by state and category */}
                      {Object.entries(
                        teachingStandards.reduce((groups: Record<string, any[]>, standard: any) => {
                          const key = `${standard.state} - ${standard.category}`;
                          if (!groups[key]) groups[key] = [];
                          groups[key].push(standard);
                          return groups;
                        }, {} as Record<string, any[]>)
                      ).map(([groupKey, standards]: [string, any[]]) => (
                        <div key={groupKey} className="space-y-2">
                          <div className="font-medium text-sm text-primary border-b border-border pb-1">
                            {groupKey}
                          </div>
                          <div className="grid grid-cols-1 gap-2 ml-2">
                            {standards.map((standard) => (
                              <div key={standard.id} className="flex items-start space-x-2">
                                <input
                                  type="checkbox"
                                  id={`standard-${standard.id}`}
                                  checked={formData.teaching_standards.includes(standard.code)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setFormData({
                                        ...formData,
                                        teaching_standards: [...formData.teaching_standards, standard.code]
                                      });
                                    } else {
                                      setFormData({
                                        ...formData,
                                        teaching_standards: formData.teaching_standards.filter(s => s !== standard.code)
                                      });
                                    }
                                  }}
                                  className="rounded mt-1"
                                />
                                <label htmlFor={`standard-${standard.id}`} className="text-sm flex-1">
                                  <div className="font-medium">{standard.title}</div>
                                  <div className="text-xs text-muted-foreground">
                                    Grades: {standard.grade_levels?.join(', ')} • Code: {standard.code}
                                  </div>
                                </label>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                      {teachingStandards.length === 0 && (
                        <div className="text-center text-muted-foreground py-4">
                          No teaching standards available
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="col-span-2">
                  <Label>Target Groups</Label>
                  <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto border rounded p-2">
                    {userGroups.map((group) => (
                      <div key={group.id} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id={`group-${group.id}`}
                          checked={formData.target_groups.includes(group.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData({
                                ...formData,
                                target_groups: [...formData.target_groups, group.id]
                              });
                            } else {
                              setFormData({
                                ...formData,
                                target_groups: formData.target_groups.filter(g => g !== group.id)
                              });
                            }
                          }}
                          className="rounded"
                        />
                        <label htmlFor={`group-${group.id}`} className="text-sm">
                          {group.name}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <Label htmlFor="target_job_types">Target Job Types</Label>
                  <Input
                    id="target_job_types"
                    value={formData.target_job_types.join(', ')}
                    onChange={(e) => setFormData({
                      ...formData, 
                      target_job_types: e.target.value.split(',').map(t => t.trim()).filter(t => t)
                    })}
                    placeholder="e.g., Teacher, Principal, Counselor"
                  />
                </div>

                <div>
                  <Label htmlFor="target_departments">Target Departments</Label>
                  <Input
                    id="target_departments"
                    value={formData.target_departments.join(', ')}
                    onChange={(e) => setFormData({
                      ...formData, 
                      target_departments: e.target.value.split(',').map(t => t.trim()).filter(t => t)
                    })}
                    placeholder="e.g., Math, Science, Special Education"
                  />
                </div>

                <div className="col-span-2">
                  <Label htmlFor="target_grade_levels">Target Grade Levels</Label>
                  <Input
                    id="target_grade_levels"
                    value={formData.target_grade_levels.join(', ')}
                    onChange={(e) => setFormData({
                      ...formData, 
                      target_grade_levels: e.target.value.split(',').map(t => t.trim()).filter(t => t)
                    })}
                    placeholder="e.g., K, 1, 2, 3-5, 6-8, 9-12"
                  />
                </div>
                
                <div className="col-span-2">
                  <Label htmlFor="justification">Justification & Need</Label>
                  <Textarea
                    id="justification"
                    value={formData.justification}
                    onChange={(e) => setFormData({...formData, justification: e.target.value})}
                    rows={3}
                    placeholder="Explain why this course is needed and how it benefits educators"
                  />
                </div>
                
                <div className="col-span-2">
                  <Label htmlFor="learning_objectives">Learning Objectives (one per line)</Label>
                  <Textarea
                    id="learning_objectives"
                    value={formData.learning_objectives}
                    onChange={(e) => setFormData({...formData, learning_objectives: e.target.value})}
                    rows={3}
                    placeholder="Enter each learning objective on a new line"
                  />
                </div>
                
                <div>
                  <Label htmlFor="target_audience">Target Audience</Label>
                  <Input
                    id="target_audience"
                    value={formData.target_audience}
                    onChange={(e) => setFormData({...formData, target_audience: e.target.value})}
                    placeholder="e.g., Elementary Teachers, Administrators"
                  />
                </div>
                
                <div>
                  <Label htmlFor="budget_estimate">Budget Estimate ($)</Label>
                  <Input
                    id="budget_estimate"
                    type="number"
                    step="0.01"
                    value={formData.budget_estimate}
                    onChange={(e) => setFormData({...formData, budget_estimate: e.target.value})}
                  />
                </div>
                
                <div className="col-span-2">
                  <Label htmlFor="materials_needed">Materials & Resources Needed</Label>
                  <Textarea
                    id="materials_needed"
                    value={formData.materials_needed}
                    onChange={(e) => setFormData({...formData, materials_needed: e.target.value})}
                    rows={2}
                    placeholder="List any special materials, technology, or resources required"
                  />
                </div>

                {/* Course Tags Section - Only for admins/instructors */}
                {(isAdmin || isInstructor) && availableTags.length > 0 && (
                  <div className="col-span-2">
                    <Label className="flex items-center gap-2">
                      <Tag className="h-4 w-4" />
                      Course Tags
                    </Label>
                    <div className="space-y-2">
                      <div className="flex flex-wrap gap-2">
                        {availableTags.map((tag) => (
                          <div key={tag.id} className="flex items-center space-x-2">
                            <Checkbox
                              id={`tag-${tag.id}`}
                              checked={formData.course_tags.includes(tag.id)}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  setFormData({
                                    ...formData,
                                    course_tags: [...formData.course_tags, tag.id]
                                  });
                                } else {
                                  setFormData({
                                    ...formData,
                                    course_tags: formData.course_tags.filter(id => id !== tag.id)
                                  });
                                }
                              }}
                            />
                            <Label 
                              htmlFor={`tag-${tag.id}`} 
                              className="text-sm font-normal cursor-pointer"
                            >
                              <Badge 
                                variant="outline" 
                                className="text-xs"
                                style={{ backgroundColor: `${tag.color}15`, borderColor: tag.color }}
                              >
                                {tag.name}
                              </Badge>
                            </Label>
                          </div>
                        ))}
                      </div>
                      {formData.course_tags.length > 0 && (
                        <div className="text-sm text-muted-foreground">
                          Selected: {formData.course_tags.length} tag{formData.course_tags.length !== 1 ? 's' : ''}
                        </div>
                      )}
                    </div>
                  </div>
                )}
 
                <div className="col-span-2">
                  <Label className="flex items-center gap-2">
                    <Paperclip className="h-4 w-4" />
                    Supporting Materials & Attachments
                  </Label>
                  <FileUploadZone
                    attachments={attachments}
                    onFilesUploaded={setAttachments}
                    onRemoveFile={(index) => {
                      const newAttachments = [...attachments];
                      newAttachments.splice(index, 1);
                      setAttachments(newAttachments);
                    }}
                  />
                </div>
              </div>
              
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit">Submit Proposal</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* New Subject Dialog */}
        <Dialog open={showNewSubjectDialog} onOpenChange={setShowNewSubjectDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Add New Subject Area</DialogTitle>
              <DialogDescription>
                Create a new subject area that can be used in course proposals
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              <div>
                <Label htmlFor="new_subject_name">Subject Name *</Label>
                <Input
                  id="new_subject_name"
                  value={newSubjectName}
                  onChange={(e) => setNewSubjectName(e.target.value)}
                  placeholder="Enter subject area name"
                  disabled={addingSubject}
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => {
                  setShowNewSubjectDialog(false);
                  setNewSubjectName('');
                }}
                disabled={addingSubject}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleAddNewSubject}
                disabled={addingSubject || !newSubjectName.trim()}
              >
                {addingSubject ? 'Adding...' : 'Add Subject'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="my-proposals">My Proposals ({myProposals.length})</TabsTrigger>
          <TabsTrigger value="all-proposals">All Proposals ({allProposals.length})</TabsTrigger>
        </TabsList>
        
        <TabsContent value="my-proposals" className="space-y-4">
          {myProposals.length === 0 ? (
            <Card>
              <CardContent className="p-6 text-center">
                <p className="text-muted-foreground">No proposals submitted yet</p>
                <Button className="mt-4" onClick={() => setIsDialogOpen(true)}>
                  Submit Your First Proposal
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {myProposals.map((proposal) => (
                <Card key={proposal.id} className="hover:shadow-md transition-shadow">
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="text-lg">{proposal.title}</CardTitle>
                        <CardDescription className="mt-1 line-clamp-2">
                          {proposal.description}
                        </CardDescription>
                      </div>
                      {getStatusBadge(proposal.status, proposal.review_notes)}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="flex gap-4 text-sm text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <Clock className="h-4 w-4" />
                        {proposal.duration_hours}h
                      </div>
                      <div className="flex items-center gap-1">
                        <Users className="h-4 w-4" />
                        Max {proposal.max_participants}
                      </div>
                      {proposal.budget_estimate && (
                        <div className="flex items-center gap-1">
                          <DollarSign className="h-4 w-4" />
                          ${proposal.budget_estimate}
                        </div>
                      )}
                      <div className="flex items-center gap-1">
                        <Calendar className="h-4 w-4" />
                        {new Date(proposal.created_at).toLocaleDateString()}
                      </div>
                    </div>
                      {/* Show reviewer feedback for non-pending proposals */}
                      {proposal.status !== 'pending' && proposal.review_notes && (
                        <div className="mt-4 p-3 bg-muted rounded-lg">
                          <div className="flex items-center gap-2 mb-2">
                            <div className={`w-2 h-2 rounded-full ${
                              proposal.status === 'approved' ? 'bg-green-500' :
                              proposal.status === 'rejected' ? 'bg-red-500' : 'bg-orange-500'
                            }`} />
                            <span className="text-sm font-medium">
                              Reviewer Feedback
                            </span>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {proposal.review_notes}
                          </p>
                        </div>
                      )}
                      
                      {/* Action buttons */}
                      <div className="mt-4 flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleEditProposal(proposal)}
                          className="flex items-center gap-1"
                        >
                          <Edit className="h-3 w-3" />
                          Edit
                        </Button>
                      </div>
                   </CardContent>
                 </Card>
               ))}
             </div>
           )}
        </TabsContent>
        
        <TabsContent value="all-proposals" className="space-y-4">
          <div className="grid gap-4">
            {allProposals.map((proposal) => (
              <Card key={proposal.id} className="hover:shadow-md transition-shadow">
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="text-lg">{proposal.title}</CardTitle>
                      <CardDescription className="mt-1 line-clamp-2">
                        {proposal.description}
                      </CardDescription>
                    </div>
                    {getStatusBadge(proposal.status, proposal.review_notes)}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex gap-4 text-sm text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <Clock className="h-4 w-4" />
                      {proposal.duration_hours}h
                    </div>
                    <div className="flex items-center gap-1">
                      <Users className="h-4 w-4" />
                      Max {proposal.max_participants}
                    </div>
                    {proposal.budget_estimate && (
                      <div className="flex items-center gap-1">
                        <DollarSign className="h-4 w-4" />
                        ${proposal.budget_estimate}
                      </div>
                    )}
                    <div className="flex items-center gap-1">
                      <Calendar className="h-4 w-4" />
                      {new Date(proposal.created_at).toLocaleDateString()}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}